import { useDecision } from '@optimizely/react-sdk';
import { useEffect, useMemo, useState } from 'react';
import { OptimizelyFlagVariant } from '../enums';
import { useAppSelector } from '../../store/hooks';
import { selectMemberSession, selectOptimizelyClientStatus } from '../../store/slices/onboardingSlice';
import { Loader } from '../Loader';
import { Logger } from '../../config/logging/logger';

export interface OptimizelyDecision {
  variationKey: string | null;
  enabled: boolean;
  variables: { [variableKey: string]: unknown };
  ruleKey: string | null;
  flagKey: string;
  reasons: string[];
  userContext?: {
    id: string | null;
    attributes?: { [attributeName: string]: unknown };
  };
}

interface useOptimizelyFlagProps {
  flag: string;
  variableName?: string;
  decisionHandler?: (variant: OptimizelyFlagVariant) => JSX.Element;
}

/**
 * How long to wait for the Optimizely user id to hydrate before falling back to
 * the decision as it stands. Without this, a user id that never resolves would
 * leave consumers waiting on the loader forever.
 */
export const userHydrationTimeoutMs = 5000;

/**
 *
 * @returns Optimizely feature desicion.
 */
export function useOptimizelyFlag({
  flag, variableName, decisionHandler,
}: useOptimizelyFlagProps): {
    decision: OptimizelyDecision,
    clientReady: boolean,
    component: JSX.Element | null,
    loaderComponent: JSX.Element,
    isDecisionReady: boolean
  } {
  let component = null;
  const loaderComponent = useMemo(() => <Loader />, []);
  /**
   * `autoUpdate` re-evaluates the decision whenever the Optimizely user or the
   * datafile changes. Without it, a decision computed before the member session
   * hydrated (user id still null, so Optimizely returns `enabled: false`) would
   * stay stuck on that value for the life of the component.
   */
  const [sdkDecision, clientReady] = useDecision(flag, { autoUpdate: true });
  const optimizelyClientStatus = useAppSelector(selectOptimizelyClientStatus);
  /**
   * When the live datafile can't be downloaded (e.g. a VPN ad-blocker), the SDK
   * never becomes ready and `useDecision` refreshes only once, at the ready
   * timeout — often before the member id is set — so it can hold
   * `enabled: false` long after (SN-1027). Ask the client directly instead: it
   * decides from the datafile saved at build time.
   *
   * The decision is cached and only recomputed when the flag, the Optimizely
   * user or the datafile changes, so it isn't re-evaluated (and impressions
   * re-sent) on every render.
   */
  const isFallbackDecision = !clientReady && optimizelyClientStatus === 'error';
  const [fallbackRevision, setFallbackRevision] = useState(0);

  useEffect(() => {
    const client = global.optimizely;
    if (!isFallbackDecision || !client) {
      return undefined;
    }
    const refresh = () => setFallbackRevision((revision) => revision + 1);
    // Picks up the member id once auth hydrates, and a datafile that downloads
    // later (useDecision stops listening once the ready timeout has passed).
    const unsubscribeUser = client.onUserUpdate(refresh);
    const configListenerId = client.notificationCenter.addNotificationListener(
      'OPTIMIZELY_CONFIG_UPDATE',
      refresh,
    );
    // No initial refresh: the memo below already decided for the current user
    // when fallback mode began, and refreshing would evaluate the flag twice.
    return () => {
      unsubscribeUser();
      client.notificationCenter.removeNotificationListener(configListenerId);
    };
  }, [isFallbackDecision]);

  const fallbackDecision = useMemo(
    () => (isFallbackDecision ? global.optimizely?.decide(flag) : undefined),
    // `fallbackRevision` is the trigger to recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flag, isFallbackDecision, fallbackRevision],
  );
  const decision = fallbackDecision ?? sdkDecision;
  const { memberId } = useAppSelector(selectMemberSession);
  const logger = useMemo(() => Logger.create('useOptimizelyFlag'), []);
  const [didUserHydrationTimeout, setDidUserHydrationTimeout] = useState(false);

  /**
   * True while we have an authenticated member but the decision was still
   * evaluated against a different (or not yet set) Optimizely user id — i.e. the
   * decision in hand predates auth hydration and cannot be trusted yet.
   */
  const isAwaitingUserHydration = !!memberId && decision?.userContext?.id !== memberId;

  useEffect(() => {
    // Reset the fallback timer whenever we start waiting on a new member.
    setDidUserHydrationTimeout(false);
  }, [memberId]);

  useEffect(() => {
    if (!isAwaitingUserHydration) {
      return undefined;
    }
    // Never block consumers indefinitely: if the user id never reaches the SDK,
    // fall back to whatever decision we have.
    const timer = setTimeout(() => setDidUserHydrationTimeout(true), userHydrationTimeoutMs);
    return () => clearTimeout(timer);
    // `memberId` is a dependency so the timer restarts when we begin waiting on a
    // different member: `isAwaitingUserHydration` can stay `true` across that
    // switch, and the elapsed time from the previous member must not carry over.
  }, [isAwaitingUserHydration, memberId]);

  /**
   * The decision is ready when the data file is loaded and the decision was
   * evaluated for the signed-in member.
   * Or there is a timeout or error that we could fall back to some values
   */
  const isDecisionReady = useMemo(() => {
    if (optimizelyClientStatus === 'error') {
      return true;
    }
    if (!clientReady) {
      return false;
    }
    return !isAwaitingUserHydration || didUserHydrationTimeout;
  }, [clientReady, optimizelyClientStatus, isAwaitingUserHydration, didUserHydrationTimeout]);

  if (decisionHandler) {
    // A fallback decision comes from the datafile saved at build time, so its
    // variables are as real as a ready client's.
    if (isDecisionReady && (clientReady || !!fallbackDecision)) {
      // happy path
      component = variableName && decision?.enabled ? decisionHandler(
        decision?.variables[variableName] as OptimizelyFlagVariant,
      ) : decisionHandler(OptimizelyFlagVariant.Default);
    } else if (isDecisionReady) {
      // Fall back to default if client timeout or error
      component = decisionHandler(OptimizelyFlagVariant.Default);
    } else {
      component = loaderComponent;
    }
  }

  useEffect(() => {
    logger.info(Logger.format('Optimizely', decision, clientReady, optimizelyClientStatus));
  }, [decision, clientReady, optimizelyClientStatus, logger]);

  return {
    decision, clientReady, isDecisionReady, component, loaderComponent,
  };
}
